import LiveActivityFeed from "../components/LiveActivityFeed";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../lib/api";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { Plus, ArrowRight, ArrowDown, TrendUp, Clock, Package, Receipt, Heart, Sparkle, ShieldCheck, CurrencyDollar, ArrowSquareOut, Gift, Buildings } from "@phosphor-icons/react";
import { outboundRetailerUrl, trackBuyNowClick } from "../lib/retailerLinks";
import { SkeletonStatCard, SkeletonGrid } from "../components/Skeleton";
import { formatDateNZ } from "../lib/format";
import FeedbackCTA from "../components/FeedbackCTA";
import CountUp from "../components/CountUp";

const PLACEHOLDERS = [
  "https://images.unsplash.com/photo-1712878696627-fd59c375dbb1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBzbWFydCUyMGhvbWUlMjBsaXZpbmclMjByb29tJTIwZWxlY3Ryb25pY3N8ZW58MHx8fHwxNzc5NTMxMzgyfDA&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/28087045/pexels-photo-28087045.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total_tracked: 0, active_drops: 0, potential_savings: 0 });
  const [household, setHousehold] = useState(null);
  const [salesEndingSoon, setSalesEndingSoon] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        api.get("/purchases"),
        api.get("/dashboard/stats"),
        api.get("/me/household-summary"),
        api.get("/dashboard/sales-ending-soon"),
      ]);
      setItems(r1.data);
      setStats(r2.data);
      setHousehold(r3.data);
      setSalesEndingSoon(r4.data?.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Hash-scroll: when the LaneNav sends us to /dashboard#tracked-list,
  // jump straight to the list section rather than landing at the top of
  // the hub. Slight delay lets the data finish rendering first so the
  // list is at its final scroll height.
  const location = useLocation();
  useEffect(() => {
    if (!location.hash || loading) return;
    const id = location.hash.replace("#", "");
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => clearTimeout(t);
  }, [location.hash, loading]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="mb-8">
          <div className="pg-overline mb-3">Your control centre</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">Dashboard<span className="text-[#002FA7]">.</span></h1>
          <p className="text-sm text-[#52525b] mt-3 max-w-2xl leading-relaxed">
            Everything you're watching, in three places. Track recent purchases for refund alerts, save items you haven't bought yet to your wishlist, and keep receipts + warranties safe in the vault.
          </p>
        </div>

        {/* SERVICE HUB — the three core jobs PriceGuard does for you,
            in one obvious row. Each card surfaces its current count and
            gives a primary 'Add' action plus an 'Open' navigation, so the
            user never has to hunt for any of the three sections. */}
        <ServiceHub
          tracked={household?.tracked_count ?? items.length}
          wishlist={household?.wishlist_count ?? 0}
          vault={household?.asset_count ?? 0}
          insurableValue={household?.total_insurable_value ?? 0}
          activeDrops={stats.active_drops}
          potentialSavings={stats.potential_savings}
        />

        {/* SALES ENDING SOON — appears only when at least one tracked
            purchase or wishlist item is in a sale window with <=3 days
            left. Compact horizontal strip so it's a glanceable nudge
            rather than another big block to scroll past. */}
        {salesEndingSoon.length > 0 && (
          <SalesEndingSoon items={salesEndingSoon} />
        )}

        {/* STAT WIDGETS — focused now on the *purchase* lane since the
            hub above already shows counts. We keep the headline savings
            number because it's the single most motivating metric on the
            page. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7] mb-10 mt-10">
          {loading ? (
            <>
              <SkeletonStatCard /><SkeletonStatCard /><SkeletonStatCard />
            </>
          ) : (
            <>
              <StatCard label="Tracked purchases" value={stats.total_tracked} icon={Package} testid="stat-tracked" />
              <StatCard label="Price movements detected" value={stats.active_drops} icon={ArrowDown} color="#D97706" testid="stat-drops" />
              <StatCard label="Protection value identified" value={stats.potential_savings} prefix="$" decimals={2} icon={TrendUp} color="#16A34A" testid="stat-savings" />
            </>
          )}
        </div>

        {/* PLAN STATUS STRIP - shown ONLY for Free users to nudge upgrade.
            Pro users get a quiet "Pro" pill in the header instead, so the
            dashboard surface stays focused on the actual workflow. */}
        {user?.plan !== "pro" && (
          <Link to="/pricing" className="border border-[#e4e4e7] bg-[#f7f7f8] p-5 mb-6 flex items-center justify-between hover:border-[#002FA7] transition-colors group" data-testid="dashboard-upgrade-cta">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-white border border-[#e4e4e7] flex items-center justify-center">
                <Sparkle size={20} weight="duotone" color="#D97706" />
              </div>
              <div>
                <div className="font-heading font-bold tracking-tight">Upgrade to Pro - $4.99/mo or $49/year (NZD)</div>
                <div className="text-sm text-[#52525b] mt-0.5">Unlimited tracking + early access to the Vault, warranty AI, and insurance exports.</div>
              </div>
            </div>
            <ArrowRight size={18} weight="bold" className="text-[#52525b] group-hover:text-[#002FA7] transition-colors" />
          </Link>
        )}

        {/* HOUSEHOLD YEAR-IN-REVIEW TILE — secondary metrics. The big
            primary jumps are in the Service Hub above; this tile is the
            "how is my household doing financially" rollup. */}
        {household && (household.tracked_count + household.vault_count > 0) && (
          <div className="border border-[#e4e4e7] bg-white mb-10" data-testid="household-summary-tile">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-[#e4e4e7]">
              <div className="bg-white p-5 md:col-span-1 flex flex-col justify-between">
                <div>
                  <div className="pg-overline">Your {household.year}</div>
                  <h3 className="font-heading text-xl font-extrabold tracking-tight mt-2">Household snapshot</h3>
                </div>
                <p className="text-[11px] font-mono-pg text-[#52525b] uppercase tracking-widest mt-4">
                  Refreshed live · NZD
                </p>
              </div>
              <div className="bg-white p-5" data-testid="household-spend-tile">
                <div className="flex items-start justify-between">
                  <div className="pg-overline">Spent this year</div>
                  <CurrencyDollar size={16} weight="duotone" color="#52525b" />
                </div>
                <div className="font-heading text-3xl font-black tracking-tighter mt-3">
                  <CountUp value={household.total_spend_this_year} prefix="$" duration={1100} />
                </div>
                <div className="text-xs text-[#52525b] mt-1 font-mono-pg">
                  across {household.tracked_count + household.vault_count} item{household.tracked_count + household.vault_count === 1 ? "" : "s"}
                </div>
              </div>
              <div className="bg-white p-5" data-testid="household-refund-tile">
                <div className="flex items-start justify-between">
                  <div className="pg-overline">Refund value spotted</div>
                  <TrendUp size={16} weight="duotone" color={household.potential_refund_value > 0 ? "#16A34A" : "#52525b"} />
                </div>
                <div className={`font-heading text-3xl font-black tracking-tighter mt-3 ${household.potential_refund_value > 0 ? "text-[#16A34A]" : ""}`}>
                  <CountUp value={household.potential_refund_value} prefix="$" decimals={2} duration={1300} />
                </div>
                <div className="text-xs text-[#52525b] mt-1 font-mono-pg">
                  {household.potential_refund_value > 0 ? "across active price-protection windows" : "no active drops right now"}
                </div>
              </div>
              <div className="bg-white p-5" data-testid="household-warranty-tile">
                <div className="flex items-start justify-between">
                  <div className="pg-overline">Items under warranty</div>
                  <ShieldCheck size={16} weight="duotone" color="#002FA7" />
                </div>
                <div className="font-heading text-3xl font-black tracking-tighter mt-3">
                  <CountUp value={household.items_under_warranty} duration={1000} />
                </div>
                <div className="text-xs text-[#52525b] mt-1 font-mono-pg">
                  we'll email 30 days before each expires
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TRACKED PURCHASES — the full grid lives on
            /dashboard/purchases now. The dashboard exposes a small
            recent-tracked strip when the user has items, and the
            EmptyState when they don't, so this surface stays focused on
            the hub + household summary rather than a long list. */}
        {!loading && items.length === 0 && <EmptyState />}

        {!loading && items.length > 0 && (
          <div className="mb-10" data-testid="dashboard-recent-tracked">
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="pg-overline">Lane 2 · Recent tracked</div>
                <h2 className="font-heading text-2xl font-bold tracking-tight mt-1">Latest tracked purchases</h2>
                <p className="text-xs text-[#52525b] mt-1">A peek at the {Math.min(3, items.length)} most recent. The full list lives on its own page.</p>
              </div>
              <Link to="/dashboard/purchases" className="text-sm font-bold inline-flex items-center gap-1.5 text-[#002FA7] hover:text-[#00207A]" data-testid="dashboard-see-all-tracked">
                See all {items.length} <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
              {items.slice(0, 3).map((p) => {
                const diff = p.purchase_price - p.current_price;
                const isDrop = diff > 0;
                return (
                  <Link key={p.purchase_id} to={`/dashboard/purchase/${p.purchase_id}`} className="bg-white p-5 hover:bg-[#f7f7f8] transition-colors flex flex-col" data-testid={`dashboard-recent-${p.purchase_id}`}>
                    <div className="pg-overline truncate">{p.retailer_name}</div>
                    <h3 className="font-heading font-bold tracking-tight truncate mt-1">{p.product_name}</h3>
                    <div className="mt-auto pt-4 flex items-center justify-between gap-2">
                      <div className="font-mono-pg text-sm">${p.current_price.toFixed(2)}</div>
                      {isDrop ? (
                        <span className="inline-flex items-center gap-1 bg-[#D97706] text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1">
                          <ArrowDown size={10} weight="bold" /> ${diff.toFixed(0)} off
                        </span>
                      ) : (
                        <span className="text-[10px] font-mono-pg uppercase tracking-widest text-[#52525b]">Watching</span>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-12">
          <FeedbackCTA variant="card" />
        </div>

        {/* LIVE ACTIVITY FEED - dashboard variant. Sits between the
            tracked-purchases list and the B2B referrer strip so users
            see they're part of a wider, active platform. */}
        <LiveActivityFeed variant="dashboard" />

        {/* SOFT B2B REFERRER STRIP — sits at the very bottom of the
            dashboard so it's seen on scroll but doesn't compete with
            primary tasks. Single line + arrow; deliberately understated
            so household users don't feel "sold to". Links straight to
            the Business card on the pricing page. */}
        <Link
          to="/pricing#business-deep-dive"
          className="mt-12 border border-[#002FA7]/30 bg-[#f7f7f8] hover:bg-white hover:border-[#002FA7] transition-colors p-4 flex items-center justify-between gap-4 group"
          data-testid="dashboard-business-strip"
        >
          <div className="flex items-center gap-3 min-w-0">
            <Buildings size={20} weight="duotone" className="text-[#002FA7] flex-shrink-0" />
            <div className="min-w-0">
              <span className="font-semibold text-sm text-[#0a0a0a]">Doing the same for your business?</span>
              <span className="text-sm text-[#52525b] ml-2">
                Early access for Xero sync, trade pricing and team dashboards is opening soon.
              </span>
            </div>
          </div>
          <span className="pg-overline text-[#002FA7] group-hover:underline whitespace-nowrap">
            Tell us →
          </span>
        </Link>
      </main>
    </div>
  );
}


// Three big "service" cards at the top of the dashboard. Each card maps
// 1:1 to one of the three things PriceGuard does: track recent purchases
// for refund alerts, watch items the user hasn't bought yet, and store
// receipts + warranties. Surfaces the count + primary metric per lane so
// the user can answer "what should I open?" in two seconds.
function ServiceHub({ tracked, wishlist, vault, insurableValue, activeDrops, potentialSavings }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7]" data-testid="service-hub">
      <HubCard
        accent="#16A34A"
        lane="Lane 1"
        title="Wishlist"
        subtitle="Items you haven't bought yet. We email you the moment they hit your target."
        icon={Heart}
        count={wishlist}
        countLabel={wishlist === 1 ? "item being watched" : "items being watched"}
        metric={null}
        addTo="/dashboard/wishlist"
        addLabel="Add a wishlist item"
        openTo="/dashboard/wishlist"
        testid="hub-wishlist"
      />
      <HubCard
        accent="#002FA7"
        lane="Lane 2"
        title="Tracked purchases"
        subtitle="Recent buys we're watching so you can claim a refund the moment the price drops."
        icon={Receipt}
        count={tracked}
        countLabel={tracked === 1 ? "item being watched" : "items being watched"}
        metric={activeDrops > 0 ? `${activeDrops} active drop${activeDrops === 1 ? "" : "s"} · $${Number(potentialSavings || 0).toFixed(2)} to claim` : null}
        addTo="/dashboard/add"
        addLabel="Add a purchase"
        openTo="/dashboard/purchases"
        testid="hub-purchases"
      />
      <HubCard
        accent="#D97706"
        lane="Lane 3"
        title="Vault"
        subtitle="Receipts, warranties and your household asset register for insurance."
        icon={ShieldCheck}
        count={vault}
        countLabel={vault === 1 ? "asset filed" : "assets filed"}
        metric={insurableValue > 0 ? `$${Number(insurableValue).toFixed(0)} insurable value` : null}
        addTo="/dashboard/vault/new"
        addLabel="Add a receipt"
        openTo="/dashboard/vault"
        testid="hub-vault"
      />
    </div>
  );
}

function HubCard({ accent, lane, title, subtitle, icon: Icon, count, countLabel, metric, addTo, addLabel, openTo, scrollToList, testid }) {
  const handleOpen = (e) => {
    if (scrollToList) {
      e.preventDefault();
      const el = document.querySelector('[data-testid="items-count"]');
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };
  return (
    <div className="bg-white p-5 sm:p-6 flex flex-col gap-4 pg-lift" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="pg-overline" style={{ color: accent }}>{lane}</div>
          <h3 className="font-heading text-xl sm:text-2xl font-black tracking-tight mt-1.5 break-words">{title}</h3>
        </div>
        <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ background: `${accent}14` }}>
          <Icon size={22} weight="duotone" color={accent} />
        </div>
      </div>
      <p className="text-sm text-[#52525b] leading-relaxed">{subtitle}</p>
      <div className="border-t border-[#e4e4e7] pt-4">
        <div className="font-heading text-3xl font-black tracking-tighter" style={{ color: accent }}>
          <CountUp value={count || 0} duration={900} />
        </div>
        <div className="text-xs text-[#52525b] font-mono-pg mt-0.5">{countLabel}</div>
        {metric && (
          <div className="text-[11px] font-mono-pg uppercase tracking-widest mt-2 text-[#0a0a0a] break-words">{metric}</div>
        )}
      </div>
      {/* Mobile-safe action stack: buttons go full-width and stack on
          phones (where two side-by-side ~140px buttons either overflow
          or shrink the click target to unusable). They sit side-by-side
          from sm: up where there's room for both labels comfortably. */}
      <div className="flex flex-col sm:flex-row gap-2 mt-auto pt-2">
        <Link
          to={addTo}
          className="flex-1 text-center font-bold px-4 py-3 text-sm inline-flex items-center justify-center gap-1.5 text-white transition-colors"
          style={{ background: accent }}
          data-testid={`${testid}-add`}
        >
          <Plus size={14} weight="bold" /> {addLabel}
        </Link>
        <Link
          to={openTo}
          onClick={handleOpen}
          className="flex-1 sm:flex-none border border-[#0a0a0a] font-bold px-4 py-3 text-sm inline-flex items-center justify-center gap-1.5 hover:bg-[#0a0a0a] hover:text-white transition-colors"
          data-testid={`${testid}-open`}
        >
          Open <ArrowRight size={14} weight="bold" />
        </Link>
      </div>
    </div>
  );
}

// Compact "time pressure" strip that only renders when there's at least
// one tracked purchase or wishlist item whose sale ends within 3 days.
// Acts as a personalised nudge instead of a noisy permanent CTA.
function SalesEndingSoon({ items }) {
  return (
    <div className="border border-[#D97706] bg-[#FFFBEB] mt-8" data-testid="sales-ending-soon">
      <div className="flex items-center justify-between px-5 pt-4">
        <div className="inline-flex items-center gap-2">
          <Clock size={16} weight="bold" className="text-[#D97706]" />
          <div className="pg-overline text-[#D97706]">Sales ending soon · {items.length} item{items.length === 1 ? "" : "s"}</div>
        </div>
        <span className="text-[10px] font-mono-pg uppercase tracking-widest text-[#52525b]">Time-sensitive</span>
      </div>
      <div className="px-5 pb-5 pt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it) => {
          const detailHref = it.type === "wishlist"
            ? `/dashboard/wishlist/${it.id}`
            : `/dashboard/purchase/${it.id}`;
          const days = Math.max(0, Number(it.sale_days_remaining || 0));
          return (
            <div key={`${it.type}-${it.id}`} className="bg-white border border-[#e4e4e7] p-3 flex flex-col gap-2" data-testid={`sale-ending-${it.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="pg-overline text-[#52525b]">{it.retailer_name} · {it.type === "wishlist" ? "Wishlist" : "Tracked"}</div>
                  <div className="font-bold text-sm tracking-tight truncate mt-0.5">{it.product_name}</div>
                </div>
                <span className="text-[10px] font-bold tracking-widest bg-[#D97706] text-white px-1.5 py-0.5 whitespace-nowrap">
                  {days === 0 ? "ENDS TODAY" : `${days}D LEFT`}
                </span>
              </div>
              <div className="text-xs text-[#52525b]">
                {it.sale_label || "Sale in progress"}
                {it.was_price && (
                  <> · <span className="line-through">${Number(it.was_price).toFixed(0)}</span> now ${Number(it.current_price).toFixed(0)}</>
                )}
              </div>
              <div className="flex gap-2">
                {it.product_url && (
                  <a
                    href={outboundRetailerUrl(it.product_url, it.retailer_id)}
                    target="_blank"
                    rel="sponsored nofollow noopener noreferrer"
                    onClick={() => trackBuyNowClick({ retailerId: it.retailer_id, productUrl: it.product_url, context: "sales_ending_soon" })}
                    className="flex-1 text-center text-[11px] font-bold uppercase tracking-widest bg-[#0a0a0a] text-white px-2 py-1.5 hover:bg-[#002FA7] transition-colors"
                    data-testid={`sale-ending-buy-${it.id}`}
                  >
                    Buy at {it.retailer_name}
                  </a>
                )}
                <Link
                  to={detailHref}
                  className="text-[11px] font-bold uppercase tracking-widest border border-[#0a0a0a] px-2 py-1.5 hover:bg-[#0a0a0a] hover:text-white transition-colors"
                  data-testid={`sale-ending-open-${it.id}`}
                >
                  Open
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function StatCard({ label, value, icon: Icon, color = "#0a0a0a", testid, prefix = "", decimals = 0 }) {
  // Numeric values animate via CountUp; string values render plain (e.g. legacy callers).
  const numeric = typeof value === "number";
  return (
    <div className="bg-white p-6 flex items-start justify-between pg-lift" data-testid={testid}>
      <div>
        <div className="pg-overline">{label}</div>
        <div className="font-heading text-4xl font-black tracking-tighter mt-3" style={{ color }}>
          {numeric ? <CountUp value={value} prefix={prefix} decimals={decimals} duration={1100} /> : value}
        </div>
      </div>
      <Icon size={28} weight="duotone" color={color} />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="border border-dashed border-[#a1a1aa] bg-[#f7f7f8] p-8 lg:p-10" data-testid="empty-state">
      <div className="text-center mb-8">
        <Receipt size={40} weight="duotone" color="#002FA7" className="mx-auto" />
        <h3 className="font-heading text-2xl font-bold tracking-tight mt-4">Let's add your first item.</h3>
        <p className="text-[#52525b] mt-2 max-w-md mx-auto">PriceGuard works three ways - pick whichever matches what you came here to do. You can mix and match later.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
        <Link to="/dashboard/wishlist" className="bg-white border border-[#e4e4e7] p-5 hover:border-[#002FA7] hover:shadow-sm transition-all group" data-testid="empty-state-wishlist">
          <Heart size={22} weight="duotone" color="#002FA7" />
          <h4 className="font-heading font-bold tracking-tight mt-3">I'm planning to buy</h4>
          <p className="text-sm text-[#52525b] mt-1.5 leading-relaxed">Get an email the moment the price drops to your target so you can buy at the right time.</p>
          <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-[#002FA7] group-hover:translate-x-0.5 transition-transform">Start a wishlist <ArrowRight size={14} weight="bold" /></span>
        </Link>
        <Link to="/dashboard/add" className="bg-white border border-[#e4e4e7] p-5 hover:border-[#002FA7] hover:shadow-sm transition-all group" data-testid="empty-state-track">
          <Receipt size={22} weight="duotone" color="#002FA7" />
          <h4 className="font-heading font-bold tracking-tight mt-3">I just bought something</h4>
          <p className="text-sm text-[#52525b] mt-1.5 leading-relaxed">We spot the price drop, draft the claim, send it to the retailer on your behalf.</p>
          <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-[#002FA7] group-hover:translate-x-0.5 transition-transform">Add a purchase <ArrowRight size={14} weight="bold" /></span>
        </Link>
        <Link to="/dashboard/vault/new" className="bg-white border border-[#e4e4e7] p-5 hover:border-[#002FA7] hover:shadow-sm transition-all group" data-testid="empty-state-vault">
          <Package size={22} weight="duotone" color="#002FA7" />
          <h4 className="font-heading font-bold tracking-tight mt-3">Store a receipt</h4>
          <p className="text-sm text-[#52525b] mt-1.5 leading-relaxed">Upload a photo or PDF. We read it with AI, file it, and remind you before the warranty expires.</p>
          <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-[#002FA7] group-hover:translate-x-0.5 transition-transform">Add a receipt <ArrowRight size={14} weight="bold" /></span>
        </Link>
      </div>
    </div>
  );
}
